import React, { useEffect, useState, useRef } from 'react';
import { Filter, Search, ChevronDown, Star, X } from 'lucide-react';
import { Unidade, BuscaAvancadaFiltros, Tag, Departamento, Prioridade, StatusTicket, Assunto } from '@/types';
import Modal from './Modal';
import { TagsService, DepartamentosService, PrioridadesService, StatusTicketsService, BuscasFavoritasService, AssuntosService } from '@/services';
import toast from 'react-hot-toast';

interface UnifiedFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Filtros básicos (locais)
  filters: {
    status: string;
    prioridade: string;
    unidade: string;
    departamento?: string;
    usuario?: string;
    assunto?: string;
    temAnexos?: string;
    temNotificacoes?: string;
    ehFavorito?: string;
    dataCriacaoInicio?: string;
    dataCriacaoFim?: string;
    ordenarPor?: string;
    ordenarDirecao?: 'asc' | 'desc';
    tags?: string[];
  };
  onFiltersChange: (filters: any) => void;
  unidades: Unidade[];
  tickets: any[];
  usuario: any;
  usuarios?: Array<{id: number, nome: string}>;
  // Busca avançada (backend)
  onAdvancedSearch?: (filtros: BuscaAvancadaFiltros) => void;
  initialAdvancedFilters?: BuscaAvancadaFiltros;
  // Modo: 'basic' para filtros locais, 'advanced' para busca no backend
  mode?: 'basic' | 'advanced';
  onModeChange?: (mode: 'basic' | 'advanced') => void;
  useAdvancedSearch?: boolean; // Se true, usa busca avançada no backend
  forceDepartamento?: boolean; // Se true, força o uso de departamento em vez de unidade (para MyTickets)
  /** Rótulo do filtro de usuário (ex.: "Quem respondeu" em Meus Tickets) */
  usuarioFilterLabel?: string;
}

export default function UnifiedFiltersModal({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  unidades,
  tickets,
  usuario,
  usuarios = [],
  onAdvancedSearch,
  initialAdvancedFilters = {},
  mode: externalMode,
  onModeChange,
  useAdvancedSearch = false,
  forceDepartamento = false,
  usuarioFilterLabel = 'Usuário'
}: UnifiedFiltersModalProps) {
  const [internalMode, setInternalMode] = useState<'basic' | 'advanced'>('basic');
  const [usuarioSearchTerm, setUsuarioSearchTerm] = useState('');
  const [isUsuarioDropdownOpen, setIsUsuarioDropdownOpen] = useState(false);
  const usuarioDropdownRef = useRef<HTMLDivElement>(null);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const tagsDropdownRef = useRef<HTMLDivElement>(null);
  
  // Estados para busca avançada
  const [advancedFilters, setAdvancedFilters] = useState<BuscaAvancadaFiltros>(initialAdvancedFilters);
  const [tags, setTags] = useState<Tag[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [prioridades, setPrioridades] = useState<Prioridade[]>([]);
  const [status, setStatus] = useState<StatusTicket[]>([]);
  const [assuntos, setAssuntos] = useState<Assunto[]>([]);
  const [showSalvar, setShowSalvar] = useState(false);
  const [nomeBuscaFavorita, setNomeBuscaFavorita] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAssuntos, setIsLoadingAssuntos] = useState(false);
  const [tagsSelecionadas, setTagsSelecionadas] = useState<string[]>(filters.tags || []);

  const mode = externalMode !== undefined ? externalMode : internalMode;
  const setMode = onModeChange || setInternalMode;
  const useAdvanced = useAdvancedSearch && onAdvancedSearch !== undefined;

  // Carregar opções quando modal abrir
  useEffect(() => {
    if (isOpen) {
      if (useAdvanced) {
        loadAdvancedOptions();
      }
      // Sempre carregar tags e assuntos
      loadTags();
      loadAssuntos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, useAdvanced]);

  const loadTags = async () => {
    try {
      const tagsData = await TagsService.getTags();
      setTags(tagsData);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadAdvancedOptions = async () => {
    try {
      const [tagsData, deptData, priorData, statusData] = await Promise.all([
        TagsService.getTags(),
        DepartamentosService.getDepartamentos(),
        PrioridadesService.getPrioridades(),
        StatusTicketsService.getStatusTickets(),
      ]);
      setTags(tagsData);
      setDepartamentos(deptData);
      setPrioridades(priorData);
      setStatus(statusData);
    } catch (error) {
      console.error('Erro ao carregar opções:', error);
    }
  };

  const loadAssuntos = async () => {
    if (!usuario) return;
    
    try {
      setIsLoadingAssuntos(true);
      let assuntosCarregados: Assunto[] = [];

      // Se usuário tem departamentos, buscar assuntos de cada departamento
      if (usuario.departamentos && usuario.departamentos.length > 0) {
        const promises = usuario.departamentos.map(dept => 
          AssuntosService.getAssuntosPorDepartamento(dept.id)
        );
        const resultados = await Promise.all(promises);
        // Combinar todos os assuntos e remover duplicatas
        const assuntosUnicos = new Map<number, Assunto>();
        resultados.flat().forEach(assunto => {
          if (!assuntosUnicos.has(assunto.id)) {
            assuntosUnicos.set(assunto.id, assunto);
          }
        });
        assuntosCarregados = Array.from(assuntosUnicos.values());
      } else if (usuario.nivel === 'dev') {
        // Dev pode ver todos os assuntos
        assuntosCarregados = await AssuntosService.getAssuntos();
      } else if (usuario.nivel === 'admin') {
        // Admin vê apenas assuntos do seu departamento
        const deptId = usuario.departamentos?.[0]?.id;
        if (deptId) {
          assuntosCarregados = await AssuntosService.getAssuntosPorDepartamento(deptId);
        }
      }

      // Ordenar por nome
      assuntosCarregados.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
      setAssuntos(assuntosCarregados);
    } catch (error) {
      console.error('Erro ao carregar assuntos:', error);
    } finally {
      setIsLoadingAssuntos(false);
    }
  };

  // Ordenar usuários por nome
  const usuariosOrdenados = [...usuarios].sort((a, b) => 
    a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
  );
  
  // Filtrar usuários baseado no termo de busca
  const usuariosFiltrados = usuariosOrdenados.filter(user =>
    user.nome.toLowerCase().includes(usuarioSearchTerm.toLowerCase())
  );
  
  // Obter nome do usuário selecionado
  const usuarioSelecionado = filters.usuario && filters.usuario !== 'all'
    ? usuariosOrdenados.find(u => u.id.toString() === filters.usuario)?.nome || 'Todos os Usuários'
    : 'Todos os Usuários';

  const handleFilterChange = (key: string, value: string | string[]) => {
    onFiltersChange({ ...filters, [key]: value });
    if (key === 'tags') {
      setTagsSelecionadas(value as string[]);
    }
  };

  const handleAdvancedFilterChange = (key: keyof BuscaAvancadaFiltros, value: any) => {
    setAdvancedFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleBasicSearch = () => {
    // Apenas fecha o modal, os filtros já são aplicados automaticamente
    onClose();
  };

  const handleAdvancedSearch = () => {
    if (onAdvancedSearch) {
      onAdvancedSearch(advancedFilters);
      onClose();
    }
  };

  const handleSalvarFavorita = async () => {
    if (!nomeBuscaFavorita.trim()) {
      toast.error('Informe um nome para a busca favorita');
      return;
    }

    try {
      setIsLoading(true);
      await BuscasFavoritasService.createBuscaFavorita({
        nome: nomeBuscaFavorita,
        filtros: advancedFilters,
      });
      toast.success('Busca favorita salva com sucesso');
      setShowSalvar(false);
      setNomeBuscaFavorita('');
    } catch (error: any) {
      console.error('Erro ao salvar busca favorita:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar busca favorita');
    } finally {
      setIsLoading(false);
    }
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (usuarioDropdownRef.current && !usuarioDropdownRef.current.contains(event.target as Node)) {
        setIsUsuarioDropdownOpen(false);
      }
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
        setIsTagsDropdownOpen(false);
      }
    };

    if (isUsuarioDropdownOpen || isTagsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUsuarioDropdownOpen, isTagsDropdownOpen]);

  // Sincronizar tags selecionadas com os filtros
  useEffect(() => {
    if (filters.tags) {
      setTagsSelecionadas(filters.tags);
    } else {
      setTagsSelecionadas([]);
    }
  }, [filters.tags]);

  // Resetar estados quando modal fechar
  useEffect(() => {
    if (!isOpen) {
      setIsUsuarioDropdownOpen(false);
      setIsTagsDropdownOpen(false);
      setUsuarioSearchTerm('');
      setShowSalvar(false);
      setNomeBuscaFavorita('');
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="text-base">Filtros e Busca</span>
        </div>
      }
      maxWidth="2xl"
    >
      <div className="p-4 sm:p-6">
        {/* Interface unificada com todos os filtros */}
        <div className="space-y-5">
          {/* Busca textual - apenas se usar busca avançada */}
          {useAdvanced && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Busca no assunto
              </label>
              <input
                type="text"
                value={advancedFilters.busca || ''}
                onChange={(e) => handleAdvancedFilterChange('busca', e.target.value)}
                placeholder="Digite palavras-chave para buscar no assunto..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              />
            </div>
          )}

          {/* Grid de filtros principais - 2 colunas em telas médias */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Filtro por Assunto */}
            {assuntos.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Assunto
                </label>
                {isLoadingAssuntos ? (
                  <div className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-center text-xs text-gray-500 dark:text-gray-400">
                    Carregando...
                  </div>
                ) : (
                  <select
                    value={filters.assunto || 'all'}
                    onChange={(e) => handleFilterChange('assunto', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                  >
                    <option value="all">Todos os Assuntos</option>
                    {assuntos.map((assunto) => (
                      <option key={assunto.id} value={assunto.nome}>
                        {assunto.nome}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Status
              </label>
              {useAdvanced ? (
                <select
                  value={Array.isArray(advancedFilters.status_id) ? '' : (advancedFilters.status_id || '')}
                  onChange={(e) => handleAdvancedFilterChange('status_id', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                >
                  <option value="">Todos os Status</option>
                  {status.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nome}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                >
                  <option value="all">Todos os Status</option>
                  <option value="Aberto">Aberto</option>
                  <option value="Em Andamento">Em Andamento</option>
                  <option value="Fechado">Fechado</option>
                </select>
              )}
            </div>

            {/* Prioridade Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Prioridade
              </label>
              {useAdvanced ? (
                <select
                  value={Array.isArray(advancedFilters.prioridade_id) ? '' : (advancedFilters.prioridade_id || '')}
                  onChange={(e) => handleAdvancedFilterChange('prioridade_id', e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                >
                  <option value="">Todas as Prioridades</option>
                  {prioridades.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={filters.prioridade}
                  onChange={(e) => handleFilterChange('prioridade', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                >
                  <option value="all">Todas as Prioridades</option>
                  <option value="Baixa">Baixa</option>
                  <option value="Média">Média</option>
                  <option value="Alta">Alta</option>
                </select>
              )}
            </div>

            {/* Filtro condicional baseado no nível do usuário ou forceDepartamento */}
            {(usuario?.nivel === 'usuario' || forceDepartamento) ? (
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Departamento
                </label>
                <select
                  value={filters.departamento || 'all'}
                  onChange={(e) => handleFilterChange('departamento', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                >
                  <option value="all">Todos os Departamentos</option>
                  {Array.from(new Set(tickets.map(t => t.departamento_nome).filter(Boolean))).map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                {/* Filtro por Unidade */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Unidade
                  </label>
                  <select
                    value={filters.unidade}
                    onChange={(e) => handleFilterChange('unidade', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                  >
                    <option value="all">Todas as Unidades</option>
                    <option value="Escritório">Escritório</option>
                    {unidades
                      .filter(unidade => unidade.ativo === 1)
                      .map(unidade => (
                        <option key={unidade.id} value={unidade.nome}>
                          {unidade.nome}
                        </option>
                      ))
                    }
                  </select>
                </div>

                {/* Filtro por Departamento específico para admin/dev */}
                {usuario?.departamentos && usuario.departamentos.length > 1 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Departamento
                    </label>
                    {useAdvanced ? (
                      <select
                        value={Array.isArray(advancedFilters.departamento_id) ? '' : (advancedFilters.departamento_id || '')}
                        onChange={(e) => handleAdvancedFilterChange('departamento_id', e.target.value ? Number(e.target.value) : undefined)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                      >
                        <option value="">Todos os Departamentos</option>
                        {departamentos.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.nome}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={filters.departamento || 'all'}
                        onChange={(e) => handleFilterChange('departamento', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                      >
                        <option value="all">Todos os Meus Departamentos</option>
                        {usuario.departamentos.map(dept => (
                          <option key={dept.id} value={dept.nome}>
                            {dept.nome}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Filtro por Usuário com busca */}
            {usuarios && usuarios.length > 0 && (
              <div ref={usuarioDropdownRef} className="relative">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {usuarioFilterLabel}
                </label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3.5 h-3.5" />
                    <input
                      type="text"
                      placeholder="Buscar usuário..."
                      value={isUsuarioDropdownOpen ? usuarioSearchTerm : usuarioSelecionado}
                      onChange={(e) => {
                        setUsuarioSearchTerm(e.target.value);
                        setIsUsuarioDropdownOpen(true);
                      }}
                      onFocus={() => {
                        setIsUsuarioDropdownOpen(true);
                        setUsuarioSearchTerm('');
                      }}
                      className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setIsUsuarioDropdownOpen(!isUsuarioDropdownOpen)}
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isUsuarioDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  {isUsuarioDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div
                        className="px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-700 text-sm"
                        onClick={() => {
                          handleFilterChange('usuario', 'all');
                          setIsUsuarioDropdownOpen(false);
                          setUsuarioSearchTerm('');
                        }}
                      >
                        <span className="text-xs text-gray-700 dark:text-gray-300">Todos os Usuários</span>
                      </div>
                      {usuariosFiltrados.length > 0 ? (
                        usuariosFiltrados.map(user => (
                          <div
                            key={user.id}
                            className={`px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm ${
                              filters.usuario === user.id.toString() ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                            }`}
                            onClick={() => {
                              handleFilterChange('usuario', user.id.toString());
                              setIsUsuarioDropdownOpen(false);
                              setUsuarioSearchTerm('');
                            }}
                          >
                            <span className="text-xs text-gray-700 dark:text-gray-300">{user.nome}</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                          Nenhum usuário encontrado
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags - apenas se usar busca avançada - linha completa */}
            {useAdvanced && tags.length > 0 && (
              <div ref={tagsDropdownRef} className="relative col-span-1 sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Etiquetas
                </label>
                <button
                  type="button"
                  onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
                  className="w-full px-3 py-2 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 flex items-center justify-between"
                >
                  <span className="truncate">
                    {advancedFilters.tags && advancedFilters.tags.length > 0
                      ? `${advancedFilters.tags.length} etiqueta(s) selecionada(s)`
                      : 'Selecione as etiquetas'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isTagsDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {tags.map((tag) => {
                      const isSelected = advancedFilters.tags?.includes(tag.id) || false;
                      return (
                        <label
                          key={tag.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const currentTags = advancedFilters.tags || [];
                              let newTags: number[];
                              if (e.target.checked) {
                                newTags = [...currentTags, tag.id];
                              } else {
                                newTags = currentTags.filter(id => id !== tag.id);
                              }
                              handleAdvancedFilterChange('tags', newTags.length > 0 ? newTags : undefined);
                            }}
                            className="w-4 h-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{tag.nome}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Datas - apenas se usar busca avançada */}
            {useAdvanced && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Data início
                  </label>
                  <input
                    type="date"
                    value={advancedFilters.data_inicio || ''}
                    onChange={(e) => handleAdvancedFilterChange('data_inicio', e.target.value || undefined)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Data fim
                  </label>
                  <input
                    type="date"
                    value={advancedFilters.data_fim || ''}
                    onChange={(e) => handleAdvancedFilterChange('data_fim', e.target.value || undefined)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                  />
                </div>
              </>
            )}

            {/* Filtros adicionais - sempre visíveis */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Tem Anexos
              </label>
              <select
                value={filters.temAnexos || 'all'}
                onChange={(e) => handleFilterChange('temAnexos', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              >
                <option value="all">Todos</option>
                <option value="sim">Com Anexos</option>
                <option value="nao">Sem Anexos</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Notificações Não Lidas
              </label>
              <select
                value={filters.temNotificacoes || 'all'}
                onChange={(e) => handleFilterChange('temNotificacoes', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              >
                <option value="all">Todos</option>
                <option value="sim">Com Notificações</option>
                <option value="nao">Sem Notificações</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Favoritos
              </label>
              <select
                value={filters.ehFavorito || 'all'}
                onChange={(e) => handleFilterChange('ehFavorito', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              >
                <option value="all">Todos</option>
                <option value="sim">Apenas Favoritos</option>
                <option value="nao">Não Favoritos</option>
              </select>
            </div>

            {/* Tags - também disponível nos filtros básicos - linha completa */}
            {!useAdvanced && tags.length > 0 && (
              <div ref={tagsDropdownRef} className="relative col-span-1 sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Etiquetas
                </label>
                <button
                  type="button"
                  onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
                  className="w-full px-3 py-2 text-sm text-left border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 flex items-center justify-between"
                >
                  <span className="truncate">
                    {tagsSelecionadas.length > 0
                      ? `${tagsSelecionadas.length} etiqueta(s) selecionada(s)`
                      : 'Selecione as etiquetas'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isTagsDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {tags.map((tag) => {
                      const isSelected = tagsSelecionadas.includes(tag.id.toString());
                      return (
                        <label
                          key={tag.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              let newTags: string[];
                              if (e.target.checked) {
                                newTags = [...tagsSelecionadas, tag.id.toString()];
                              } else {
                                newTags = tagsSelecionadas.filter(id => id !== tag.id.toString());
                              }
                              setTagsSelecionadas(newTags);
                              handleFilterChange('tags', newTags);
                            }}
                            className="w-4 h-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer"
                          />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{tag.nome}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Datas de criação - lado a lado */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Data de Criação (Início)
              </label>
              <input
                type="date"
                value={filters.dataCriacaoInicio || ''}
                onChange={(e) => handleFilterChange('dataCriacaoInicio', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Data de Criação (Fim)
              </label>
              <input
                type="date"
                value={filters.dataCriacaoFim || ''}
                onChange={(e) => handleFilterChange('dataCriacaoFim', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              />
            </div>

            {/* Ordenação */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Ordenar Por
              </label>
              <select
                value={filters.ordenarPor || 'created_at'}
                onChange={(e) => handleFilterChange('ordenarPor', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              >
                <option value="created_at">Data de Criação</option>
                <option value="updated_at">Data de Atualização</option>
                <option value="prioridade_id">Prioridade</option>
                <option value="status_id">Status</option>
                <option value="assunto">Assunto</option>
                <option value="usuario_nome">Usuário</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Direção
              </label>
              <select
                value={filters.ordenarDirecao || 'desc'}
                onChange={(e) => handleFilterChange('ordenarDirecao', e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
              >
                <option value="desc">Decrescente (Mais Recente)</option>
                <option value="asc">Crescente (Mais Antigo)</option>
              </select>
            </div>
          </div>

          {/* Salvar busca favorita - apenas se usar busca avançada */}
          {useAdvanced && showSalvar && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <input
                type="text"
                placeholder="Nome da busca favorita"
                value={nomeBuscaFavorita}
                onChange={(e) => setNomeBuscaFavorita(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-2 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500"
                disabled={isLoading}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSalvarFavorita}
                  disabled={!nomeBuscaFavorita.trim() || isLoading}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Salvar
                </button>
                <button
                  onClick={() => {
                    setShowSalvar(false);
                    setNomeBuscaFavorita('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700">
        {useAdvanced && (
          <button
            onClick={() => setShowSalvar(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          >
            <Star className="w-3.5 h-3.5" />
            Salvar como favorita
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {useAdvanced && (
            <button
              onClick={() => {
                setAdvancedFilters({});
                if (onAdvancedSearch) {
                  onAdvancedSearch({});
                }
              }}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Limpar
            </button>
          )}
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Fechar
          </button>
          {useAdvanced ? (
            <button
              onClick={handleAdvancedSearch}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              <Search className="w-3.5 h-3.5" />
              Buscar
            </button>
          ) : (
            <button
              onClick={handleBasicSearch}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              Aplicar Filtros
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
